import React, { useState, useRef } from 'react';
import { ReactTyped } from 'react-typed';
import emailjs from 'emailjs-com';
import ReCAPTCHA from 'react-google-recaptcha';
import { RiChatSmile3Line } from 'react-icons/ri';
import bgImage from '../assets/ai.jpg';
import './Contact.css';

const ContactForm = () => {
  const formRef = useRef();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    service: '',
  });

  const [captchaValue, setCaptchaValue] = useState(null);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [hover, setHover] = useState(false);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const onCaptchaChange = (value) => {
    setCaptchaValue(value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setConfirmationMessage('');
    setErrorMessage('');

    if (!captchaValue) {
      setErrorMessage('❌ Please complete the CAPTCHA before submitting.');
      return;
    }

    // Send email with controlled form values
    emailjs.send(
      'service_vgbb0cl',
      'template_jbm270t',
      {
        user_name: formData.name,
        user_email: formData.email,
        user_subject: formData.service,
        user_message: formData.message,
      },
      'sM9CY_sGBvdqX7eP8'
    ).then(() => {
      // Send confirmation email
      emailjs.send(
        'service_vgbb0cl',
        'template_880obwp',
        {
          user_email: formData.email,
          user_name: formData.name,
        },
        'sM9CY_sGBvdqX7eP8'
      ).then(() => {
        setConfirmationMessage('✅ Message sent successfully! Confirmation email sent.');
        setErrorMessage('');
        setFormData({ name: '', email: '', message: '', service: '' });
        setCaptchaValue(null);
      }).catch((error) => {
        console.error('Error sending confirmation email:', error.text);
        setConfirmationMessage('✅ Message sent! But failed to send confirmation email.');
        setFormData({ name: '', email: '', message: '', service: '' });
        setCaptchaValue(null);
      });
    }).catch((error) => {
      console.error('Error sending email:', error.text);
      setErrorMessage('❌ Failed to send message. Please try again later.');
    });
  };


  const containerStyle = {
    backgroundImage: `linear-gradient(to right, rgba(15, 23, 42, 0.92), rgba(30, 41, 59, 0.92)), url(${bgImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed',
    padding: '20px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '80vh',
  };

  const overlayStyle = {
    backgroundColor: '#ffffff',
    padding: '50px 40px',
    borderRadius: '12px',
    maxWidth: '700px',
    width: '100%',
    boxShadow: '0 12px 28px rgba(0, 0, 0, 0.2)',
  };

  const headingStyle = {
    textAlign: 'center',
    marginBottom: '10px',
    fontSize: '20px',
    color: '#64748b',
  };

  const animatedHeadingStyle = {
    textAlign: 'center',
    marginBottom: '30px',
    fontSize: '32px',
    color: '#1e293b',
    fontWeight: '600',
  };

  const rowStyle = {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap',
  };

  const inputStyle = {
    flex: 1,
    padding: '12px 16px',
    marginBottom: '20px',
    border: '1px solid #ccc',
    borderRadius: '8px',
    fontSize: '16px',
    fontFamily: 'inherit',
    outline: 'none',
    minWidth: '200px',
  };

  const textareaStyle = {
    ...inputStyle,
    width: '100%',
    height: '130px',
    resize: 'vertical',
  };

  const buttonStyle = {
    background: hover ? '#334155' : '#1e293b',
    color: '#fff',
    padding: '14px 28px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
    transition: 'background 0.3s ease',
  };

  return (
    <div style={containerStyle}>
      <div style={overlayStyle}>
        <div style={{ textAlign: 'center', marginBottom: '15px' }}>
          <RiChatSmile3Line size={48} color="#6366f1" />
        </div>
        <h2 style={headingStyle}>Want to talk about your project?</h2>
        <h2 style={animatedHeadingStyle}>
          <ReactTyped
            strings={['Get in Touch', 'Let’s Collaborate', 'We’re Here to Help']}
            typeSpeed={60}
            backSpeed={40}
            backDelay={1500}
            loop
          />
        </h2>

        <form ref={formRef} onSubmit={handleSubmit}>
          <div style={rowStyle}>
            <input
              type="text"
              name="name"
              placeholder="Your Name"
              style={inputStyle}
              value={formData.name}
              onChange={handleChange}
              required
            />
            <input
              type="email"
              name="email"
              placeholder="Your Email"
              style={inputStyle}
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <select
            name="service"
            style={inputStyle}
            value={formData.service}
            onChange={handleChange}
            required
          >
            <option value="">Select Service Required</option>
            <option value="Web Development">Web Development</option>
            <option value="Mobile App Development">Mobile App Development</option>
            <option value="AI Solutions">AI Solutions</option>
            <option value="Cloud Services">Cloud Services</option>
            <option value="IT Consulting">IT Consulting</option>
          </select>

          <textarea
            name="message"
            placeholder="Your Message"
            style={textareaStyle}
            value={formData.message}
            onChange={handleChange}
            required
          />

          {/* Google reCAPTCHA */}
          <ReCAPTCHA
            sitekey="6LevZ0ArAAAAAF9LQri_Ra0HSJKsnUNa2UlCuFDo"
            onChange={onCaptchaChange}
          />

          <button
            type="submit"
            style={buttonStyle}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
          >
            Send Message
          </button>

          {confirmationMessage && (
            <div style={{
              marginTop: '15px',
              padding: '15px',
              backgroundColor: '#d1fae5',
              color: '#065f46',
              border: '1px solid #10b981',
              borderRadius: '8px',
              fontWeight: '600',
            }}>
              {confirmationMessage}
            </div>
          )}

          {errorMessage && (
            <div style={{
              marginTop: '15px',
              padding: '15px',
              backgroundColor: '#fee2e2',
              color: '#991b1b',
              border: '1px solid #ef4444',
              borderRadius: '8px',
              fontWeight: '600',
            }}>
              {errorMessage}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default ContactForm;
